import { apiRequest } from '../../../shared/services/apiClient';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';
import type {
  AiCommandCatalogItem,
  AiCommandDefinition,
  UpsertAiCommandRequest,
} from '../types/Command';

const normalizeCommand = (value: unknown): AiCommandDefinition => {
  const row = (value ?? {}) as Record<string, unknown>;
  return {
    commandId: String(row.commandId ?? ''),
    trigger: String(row.trigger ?? ''),
    displayName: String(row.displayName ?? ''),
    description: String(row.description ?? ''),
    skillId: row.skillId ? String(row.skillId) : null,
    extraInstruction: String(row.extraInstruction ?? ''),
    isPublished: Boolean(row.isPublished),
    assignedUseCaseIds: Array.isArray(row.assignedUseCaseIds)
      ? row.assignedUseCaseIds.map(String) as AiPromptUseCaseId[]
      : [],
  };
};

const normalizeCatalogItem = (value: unknown): AiCommandCatalogItem => {
  const row = (value ?? {}) as Record<string, unknown>;
  return {
    commandId: String(row.commandId ?? ''),
    trigger: String(row.trigger ?? ''),
    displayName: String(row.displayName ?? ''),
    description: String(row.description ?? ''),
  };
};

export const listAiCommands = async (): Promise<AiCommandDefinition[]> => {
  const result = await apiRequest<unknown[]>('/api/v1/ai/commands', { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizeCommand) : [];
};

export const listPublishedAiCommands = async (): Promise<AiCommandCatalogItem[]> => {
  const result = await apiRequest<unknown[]>('/api/v1/ai/commands/catalog', { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizeCatalogItem) : [];
};

export const createAiCommand = async (request: UpsertAiCommandRequest): Promise<AiCommandDefinition> => {
  const result = await apiRequest<unknown>('/api/v1/ai/commands', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return normalizeCommand(result);
};

export const updateAiCommand = async (
  commandId: string,
  request: UpsertAiCommandRequest,
): Promise<AiCommandDefinition> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/commands/${encodeURIComponent(commandId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
  );
  return normalizeCommand(result);
};

export const deleteAiCommand = async (commandId: string): Promise<void> => {
  await apiRequest<void>(`/api/v1/ai/commands/${encodeURIComponent(commandId)}`, { method: 'DELETE' });
};
